'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import { useTranslation } from '../lib/i18n';
import { useToast } from './Toast';
import { useExitAnimation } from '../lib/useExitAnimation';
import { apiFetch, ApiError, API_BASE } from '../lib/api';
import { usePersistedState } from '../lib/usePersistedState';
import { sanitizeHtml, resizeImageDataUrl } from '../app/email/utils';

// ── Types ────────────────────────────────────────────────────────────────────

interface EmailAccountDTO {
  id: number;
  email: string;
  provider: string;
  accountLabel?: string | null;
  connected: boolean;
  wildcard?: boolean;
  displayName?: string | null;
  signatureText?: string | null;
  signatureHtml?: string | null;
}

interface EmailIdentityDTO {
  id: number;
  accountId: number;
  email: string;
  displayName?: string | null;
  signatureText?: string | null;
  signatureHtml?: string | null;
}

export type ComposeRequest = {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  bodyText?: string;
  bodyHtml?: string;
  attachments?: File[];
  /** Invoice id — backend marks it as emailed after successful send. */
  invoiceId?: number;
  /** Číslo faktury pro titulek sekce příloh. */
  invoiceNumber?: string;
  /** Přílohy faktury generované na serveru při odeslání (PDF/ISDOC/ISDOCX/ZUGFeRD). */
  invoiceAttach?: { pdf?: boolean; isdoc?: boolean; isdocx?: boolean; zugferd?: boolean; reportType?: string };
  /** When true together with invoiceId, backend counts this as a payment reminder. */
  reminder?: boolean;
  /** Called after the email was successfully sent (e.g. to refresh related UI). */
  onSent?: () => void;
};

type ComposeState = {
  to: string;
  cc: string;
  bcc: string;
  replyTo: string;
  subject: string;
  bodyText: string;
  bodyHtml: string;
  fromEmail: string | null;
  fromName: string | null;
};

type ComposeModalContextValue = {
  openCompose: (req: ComposeRequest) => void;
};

const ComposeModalContext = createContext<ComposeModalContextValue | null>(null);

export function useComposeModal() {
  const ctx = useContext(ComposeModalContext);
  return ctx ?? { openCompose: (_req: ComposeRequest) => {} };
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function ComposeModalProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState<ComposeRequest | null>(null);
  const exiting = useExitAnimation(open);
  const openCompose = useCallback((req: ComposeRequest) => {
    setInitial(req);
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
    // initial nemazat — potřebujeme ho během exit animace
  }, []);
  return (
    <ComposeModalContext.Provider value={{ openCompose }}>
      {children}
      {(open || exiting) && <ComposeModalContent initial={initial} onClose={handleClose} closing={!open} />}
    </ComposeModalContext.Provider>
  );
}

// ── Modal Content ─────────────────────────────────────────────────────────────

function ComposeModalContent({ initial, onClose, closing = false }: { initial: ComposeRequest | null; onClose: () => void; closing?: boolean }) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  // ── Accounts & identities ──────────────────────────────────────────────────
  const [accounts, setAccounts] = useState<EmailAccountDTO[]>([]);
  const [identities, setIdentities] = useState<EmailIdentityDTO[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<number | null>(null);
  const [accountsLoaded, setAccountsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<EmailAccountDTO[]>('/api/user/email/accounts')
      .then((data) => {
        if (cancelled) return;
        setAccounts(data || []);
        if (data && data.length > 0) setSelectedAccount(data[0].id);
        setAccountsLoaded(true);
      })
      .catch(() => setAccountsLoaded(true));
    return () => { cancelled = true; };
  }, []);

  const selectedAccountObj = useMemo(
    () => accounts.find((a) => a.id === selectedAccount) ?? null,
    [accounts, selectedAccount],
  );

  useEffect(() => {
    if (!selectedAccountObj?.wildcard) { setIdentities([]); return; }
    let cancelled = false;
    apiFetch<EmailIdentityDTO[]>(`/api/user/email/accounts/${selectedAccount}/identities`)
      .then((data) => { if (!cancelled) setIdentities(data || []); })
      .catch(() => { if (!cancelled) setIdentities([]); });
    return () => { cancelled = true; };
  }, [selectedAccountObj, selectedAccount]);

  // ── Compose state ──────────────────────────────────────────────────────────
  const [compose, setCompose] = useState<ComposeState>({
    to: '', cc: '', bcc: '', replyTo: '', subject: '', bodyText: '', bodyHtml: '', fromEmail: null, fromName: null,
  });
  const [files, setFiles] = useState<File[]>([]);
  // Přílohy faktury generované na serveru — volby se pamatují sdíleně s dřívějším send modálem
  const [invAttach, setInvAttach] = usePersistedState<{ template: string; pdf: boolean; isdoc: boolean; isdocx: boolean; zugferd: boolean } | null>('invoice-send', 'options', null);
  const [fields, setFields] = useState({ cc: false, bcc: false, replyTo: false });
  const [showFieldMenu, setShowFieldMenu] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [maximized, setMaximized] = useState(false);
  const [markdown, setMarkdown] = useState(false);
  const [preview, setPreview] = useState(false);
  const [returnReceipt, setReturnReceipt] = useState(false);
  const [priority, setPriority] = useState<'highest' | 'high' | 'normal' | 'low' | 'lowest'>('normal');
  const [showDiscard, setShowDiscard] = useState(false);
  const discardExiting = useExitAnimation(showDiscard);
  const [contactSuggestions, setContactSuggestions] = useState<{ name: string | null; email: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<'to' | 'cc' | 'bcc' | null>(null);
  const [customRect, setCustomRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  const editorRef = useRef<HTMLDivElement | null>(null);
  const toRef = useRef<HTMLInputElement | null>(null);
  const searchAbort = useRef<AbortController | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);

  // ── Initialize from request ────────────────────────────────────────────────
  useEffect(() => {
    if (!initial) return;
    setCompose({
      to: initial.to || '',
      cc: initial.cc || '',
      bcc: initial.bcc || '',
      replyTo: '',
      subject: initial.subject || '',
      bodyText: initial.bodyText || '',
      bodyHtml: initial.bodyHtml || '',
      fromEmail: null,
      fromName: null,
    });
    setFiles(initial.attachments || []);
    // Přílohy faktury — persisted volba má přednost (jako dřívější send modal), initial jen default
    if (initial.invoiceAttach) {
      setInvAttach(prev => ({
        template: prev?.template || initial.invoiceAttach?.reportType || 'issued-invoice',
        pdf: prev?.pdf ?? initial.invoiceAttach?.pdf ?? true,
        isdoc: prev?.isdoc ?? initial.invoiceAttach?.isdoc ?? false,
        isdocx: prev?.isdocx ?? initial.invoiceAttach?.isdocx ?? false,
        zugferd: prev?.zugferd ?? initial.invoiceAttach?.zugferd ?? false,
      }));
    }
    if (initial.cc) setFields((p) => ({ ...p, cc: true }));
    if (initial.bcc) setFields((p) => ({ ...p, bcc: true }));
    // Set editor innerHTML after render, then auto-insert signature
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = initial.bodyHtml || '';
      }
      if (!initial.to && toRef.current) toRef.current.focus();
    }, 50);
  }, [initial]);

  // ── Active signature (identity > account) ───────────────────────────────────
  const activeSignature = useMemo(() => {
    if (compose.fromEmail) {
      const ident = identities.find((i) => i.email === compose.fromEmail);
      if (ident && (ident.signatureHtml || ident.signatureText)) {
        return { html: ident.signatureHtml || '', text: ident.signatureText || '' };
      }
    }
    return {
      html: selectedAccountObj?.signatureHtml || '',
      text: selectedAccountObj?.signatureText || '',
    };
  }, [compose.fromEmail, identities, selectedAccountObj]);

  // ── Contact suggestions ────────────────────────────────────────────────────
  const updateSuggestions = useCallback((value: string, field: 'to' | 'cc' | 'bcc') => {
    const parts = value.split(/[,;]+/);
    const lastPart = parts[parts.length - 1].trim().toLowerCase();
    if (lastPart.length < 1) { setContactSuggestions([]); setShowSuggestions(null); return; }
    const alreadyEntered = new Set(parts.slice(0, -1).map((p) => p.trim().toLowerCase()).filter(Boolean));
    if (searchAbort.current) searchAbort.current.abort();
    if (lastPart.length < 2) { setContactSuggestions([]); setShowSuggestions(null); return; }
    const controller = new AbortController();
    searchAbort.current = controller;
    const timer = setTimeout(async () => {
      try {
        const results = await apiFetch<{ name: string | null; email: string }[]>(
          `/api/user/obchod/accounts/email-contacts?search=${encodeURIComponent(lastPart)}`,
          { silent: true, signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        const merged = results.filter((r) => !alreadyEntered.has(r.email.toLowerCase())).slice(0, 8);
        if (merged.length > 0) { setContactSuggestions(merged); setShowSuggestions(field); }
        else { setContactSuggestions([]); setShowSuggestions(null); }
      } catch { /* ignore */ }
    }, 250);
    controller.signal.addEventListener('abort', () => clearTimeout(timer));
  }, []);

  const applySuggestion = useCallback((s: { name: string | null; email: string }, field: 'to' | 'cc' | 'bcc') => {
    setCompose((prev) => {
      const parts = prev[field].split(/[,;]+/);
      parts[parts.length - 1] = s.email;
      return { ...prev, [field]: parts.join(', ') };
    });
    setContactSuggestions([]);
    setShowSuggestions(null);
  }, []);

  // ── Signature ───────────────────────────────────────────────────────────────
  const insertSignature = useCallback(() => {
    const sigText = activeSignature.text;
    const sigHtml = activeSignature.html;
    if (!sigText && !sigHtml) return;
    if (markdown) {
      setCompose((prev) => {
        const prefix = prev.bodyText ? '\n\n\n-- \n' : '\n-- \n';
        const newText = prev.bodyText + prefix + sigText;
        const html = marked.parse(newText, { async: false }) as string;
        return { ...prev, bodyText: newText, bodyHtml: html };
      });
    } else if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertHTML', false, `<div class="signature">${sigHtml}</div>`);
      setCompose((p) => ({ ...p, bodyHtml: editorRef.current!.innerHTML, bodyText: editorRef.current!.innerText }));
    } else {
      setCompose((prev) => {
        const textPrefix = prev.bodyText ? '\n\n\n-- \n' : '\n-- \n';
        const htmlPrefix = prev.bodyHtml ? '<br/><br/><br/>' : '';
        return { ...prev, bodyText: prev.bodyText + textPrefix + sigText, bodyHtml: prev.bodyHtml + htmlPrefix + `<div class="signature">${sigHtml}</div>` };
      });
    }
  }, [activeSignature, markdown]);

  // ── Image insertion ────────────────────────────────────────────────────────
  const insertImageAtCaret = useCallback((src: string) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    const attrs = `src="${src.replace(/"/g, '"')}" style="max-width:100%; height:auto;"`;
    document.execCommand('insertHTML', false, `<img ${attrs} />`);
    setCompose((p) => ({ ...p, bodyHtml: editorRef.current!.innerHTML, bodyText: editorRef.current!.innerText }));
  }, []);

  const insertImageFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target?.result as string;
      if (!dataUrl) return;
      try { insertImageAtCaret(await resizeImageDataUrl(dataUrl, 1024)); }
      catch { insertImageAtCaret(dataUrl); }
    };
    reader.readAsDataURL(file);
  }, [insertImageAtCaret]);

  const handleEditorDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
    Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/')).forEach(insertImageFile);
  }, [insertImageFile]);

  const handleEditorPaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const imgs = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
    if (imgs.length === 0) return;
    e.preventDefault();
    imgs.forEach(insertImageFile);
  }, [insertImageFile]);

  // ── Dirty check ────────────────────────────────────────────────────────────
  const isDirty = useMemo(() =>
    !!(compose.to.trim() || compose.cc.trim() || compose.bcc.trim() || compose.subject.trim() || compose.bodyText.trim() || files.length > 0),
    [compose, files],
  );

  const handleClose = useCallback(() => {
    if (sending) return;
    if (isDirty) setShowDiscard(true);
    else onClose();
  }, [sending, isDirty, onClose]);

  // ── Send ────────────────────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    if (!selectedAccount) return;
    const hasEmptySubject = !compose.subject.trim();
    const hasEmptyBody = !compose.bodyText.trim() && !compose.bodyHtml.trim();
    if (hasEmptySubject || hasEmptyBody) {
      const msg = hasEmptySubject && hasEmptyBody ? t('emailConfirmEmptyBoth')
        : hasEmptySubject ? t('emailConfirmEmptySubject') : t('emailConfirmEmptyBody');
      if (!window.confirm(msg)) return;
    }
    setSending(true);
    try {
      let result: { sent: boolean };
      const hasInvoiceAttach = !!(initial?.invoiceId != null && invAttach && (invAttach.pdf || invAttach.isdoc || invAttach.isdocx || invAttach.zugferd));
      if (files.length > 0 || hasInvoiceAttach) {
        const formData = new FormData();
        formData.append('to', compose.to);
        if (fields.cc && compose.cc) formData.append('cc', compose.cc);
        if (fields.bcc && compose.bcc) formData.append('bcc', compose.bcc);
        formData.append('subject', compose.subject);
        if (compose.bodyText) formData.append('bodyText', compose.bodyText);
        if (compose.bodyHtml) formData.append('bodyHtml', compose.bodyHtml);
        if (compose.fromEmail) formData.append('fromEmail', compose.fromEmail);
        if (compose.fromName) formData.append('fromName', compose.fromName);
        formData.append('returnReceipt', String(returnReceipt));
        formData.append('priority', priority);
        if (initial?.invoiceId != null) formData.append('invoiceId', String(initial.invoiceId));
        if (initial?.reminder) formData.append('reminder', 'true');
        if (hasInvoiceAttach && invAttach) {
          formData.append('invoiceAttachPdf', String(invAttach.pdf));
          formData.append('invoiceAttachIsdoc', String(invAttach.isdoc));
          formData.append('invoiceAttachIsdocx', String(invAttach.isdocx));
          formData.append('invoiceAttachZugferd', String(invAttach.zugferd));
          if (invAttach.template) formData.append('invoiceReportType', invAttach.template);
        }
        for (const file of files) formData.append('files', file);
        setUploadProgress(0);
        result = await new Promise<{ sent: boolean }>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.onprogress = (e) => { if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100)); };
          xhr.onload = () => { setUploadProgress(null); if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText)); else reject(new Error(`HTTP ${xhr.status}`)); };
          xhr.onerror = () => { setUploadProgress(null); reject(new Error('Network error')); };
          xhr.open('POST', `${API_BASE}/api/user/email/accounts/${selectedAccount}/send-with-attachments`);
          xhr.withCredentials = true;
          xhr.send(formData);
        });
      } else {
        result = await apiFetch<{ sent: boolean }>(`/api/user/email/accounts/${selectedAccount}/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: compose.to,
            cc: fields.cc ? compose.cc || undefined : undefined,
            bcc: fields.bcc ? compose.bcc || undefined : undefined,
            subject: compose.subject,
            bodyText: compose.bodyText,
            bodyHtml: compose.bodyHtml || undefined,
            fromEmail: compose.fromEmail || undefined,
            fromName: compose.fromName || undefined,
            returnReceipt,
            priority,
            invoiceId: initial?.invoiceId ?? null,
            reminder: initial?.reminder || undefined,
          }),
        });
      }
      if (result.sent) {
        showToast(t('emailSent'), 'success');
        initial?.onSent?.();
        onClose();
      } else {
        showToast(t('emailSendFailed'), 'error');
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error';
      showToast(msg, 'error');
    } finally {
      setSending(false);
    }
  }, [selectedAccount, compose, files, fields, returnReceipt, priority, showToast, t, onClose, initial]);

  // ── Save draft ─────────────────────────────────────────────────────────────
  const handleSaveDraft = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      let result: { saved: boolean };
      if (files.length > 0) {
        const formData = new FormData();
        formData.append('to', compose.to || '');
        if (fields.cc && compose.cc) formData.append('cc', compose.cc);
        if (fields.bcc && compose.bcc) formData.append('bcc', compose.bcc);
        formData.append('subject', compose.subject || '');
        if (compose.bodyText) formData.append('bodyText', compose.bodyText);
        if (compose.bodyHtml) formData.append('bodyHtml', compose.bodyHtml);
        for (const file of files) formData.append('files', file);
        result = await new Promise<{ saved: boolean }>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText)); else reject(new Error(`HTTP ${xhr.status}`)); };
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.open('POST', `${API_BASE}/api/user/email/accounts/${selectedAccount}/draft-with-attachments`);
          xhr.withCredentials = true;
          xhr.send(formData);
        });
      } else {
        result = await apiFetch<{ saved: boolean }>(`/api/user/email/accounts/${selectedAccount}/draft`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: compose.to || undefined,
            cc: fields.cc ? compose.cc || undefined : undefined,
            bcc: fields.bcc ? compose.bcc || undefined : undefined,
            subject: compose.subject || undefined,
            bodyText: compose.bodyText,
            bodyHtml: compose.bodyHtml || undefined,
          }),
        });
      }
      if (result.saved) { showToast(t('emailDraftSaved'), 'success'); onClose(); }
      else showToast(t('emailDraftSaveFailed'), 'error');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error';
      showToast(msg, 'error');
    }
  }, [selectedAccount, compose, files, fields, showToast, t, onClose]);

  // ── Escape to close ────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending && !showDiscard) handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose, sending, showDiscard]);

  // ── Resize from top-right corner ────────────────────────────────────────────
  const startResize = useCallback((e: React.MouseEvent) => {
    if (maximized || !modalRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = modalRef.current.getBoundingClientRect();
    const start = { x: e.clientX, y: e.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'nwse-resize';
    const onMove = (ev: MouseEvent) => {
      const width = Math.min(Math.max(start.width + (ev.clientX - start.x), 560), window.innerWidth - start.left - 8);
      const height = Math.min(Math.max(start.height + (ev.clientY - start.y), 400), window.innerHeight - start.top - 8);
      setCustomRect({ left: start.left, top: start.top, width, height });
    };
    const onUp = () => {
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [maximized]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const sizeClass = maximized ? 'w-full h-full md:w-[95vw] md:h-[90vh]' : 'w-[1100px] h-[700px] max-w-full max-h-full';
  const customStyle = !maximized && customRect
    ? { position: 'fixed' as const, left: customRect.left, top: customRect.top, width: customRect.width, height: customRect.height }
    : undefined;

  return (
    <div className={`fixed inset-0 bg-bark/30 flex items-center justify-center z-50 p-2 ${closing ? 'animate-backdropOut' : 'animate-backdropIn'}`} onMouseDown={handleClose}>
      <div
        ref={modalRef}
        key="compose-modal"
        className={`bg-cream flex flex-col relative ${customStyle ? '' : sizeClass} rounded-lg shadow-xl ${sending ? 'pointer-events-none' : ''} ${closing ? 'animate-modalOut' : 'animate-modalIn'}`}
        style={customStyle}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-3 border-b border-bark/10">
          <h2 className="text-sm font-bold text-bark">{t('emailCompose')}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={handleClose} disabled={sending} className="px-3 py-1 text-xs text-wood hover:text-bark disabled:opacity-50">{t('emailCancel')}</button>
            <button onClick={handleSaveDraft} disabled={!compose.subject.trim()} className="px-3 py-1 text-xs text-wood border border-bark/20 rounded hover:bg-bark/5 transition disabled:opacity-50" title={t('emailSaveDraft')}>{t('emailSaveDraft')}</button>
            <button
              onClick={handleSend}
              disabled={!compose.to || !compose.subject || sending}
              title={(!compose.to || !compose.subject) ? t('emailSendDisabledHint') : t('emailSend')}
              className="px-3 py-1 text-xs bg-moss text-cream rounded hover:bg-moss/80 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {sending ? (<><span className="inline-block w-3 h-3 border-2 border-cream/30 border-t-cream rounded-full animate-spin" />{t('emailSending')}</>) : t('emailSend')}
            </button>
            <div className="w-px h-5 bg-bark/20 mx-1" />
            <button onClick={() => setMaximized((v) => !v)} className="text-wood hover:text-bark text-sm" title={maximized ? t('emailRestore') : t('emailMaximize')}>{maximized ? '🗗' : '🗖'}</button>
            <button onClick={handleClose} className="text-wood hover:text-bark">✕</button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-col sm:flex-row flex-1 overflow-hidden">
          <div className="flex-1 flex flex-col gap-3 p-4 overflow-y-auto">
            {/* From selector */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-wood whitespace-nowrap">{t('emailFrom')}:</label>
              <select
                value={`${selectedAccount ?? ''}:${compose.fromEmail || ''}`}
                onChange={(e) => {
                  const [accId, identEmail] = e.target.value.split(':');
                  const acc = accounts.find((a) => a.id === Number(accId));
                  if (acc) {
                    setSelectedAccount(acc.id);
                    if (identEmail) {
                      const ident = identities.find((i) => i.email === identEmail);
                      setCompose((p) => ({ ...p, fromEmail: ident?.email || null, fromName: ident?.displayName || null }));
                    } else {
                      setCompose((p) => ({ ...p, fromEmail: null, fromName: null }));
                    }
                  }
                }}
                className="flex-1 px-3 py-1.5 border border-bark/20 rounded text-sm bg-cream text-bark"
              >
                {accounts.map((acc) => (
                  <optgroup key={acc.id} label={acc.email}>
                    <option value={`${acc.id}:`}>{acc.displayName ? `${acc.displayName} <${acc.email}>` : acc.email}</option>
                    {acc.wildcard && identities.filter((i) => i.accountId === acc.id).map((ident) => (
                      <option key={ident.id} value={`${acc.id}:${ident.email}`}>{ident.displayName ? `${ident.displayName} <${ident.email}>` : ident.email}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* To */}
            <div className="relative flex items-center gap-0.5">
              <input
                ref={toRef}
                type="text"
                placeholder={t('emailTo')}
                value={compose.to}
                onChange={(e) => { setCompose((p) => ({ ...p, to: e.target.value })); updateSuggestions(e.target.value, 'to'); }}
                onBlur={() => setTimeout(() => setShowSuggestions(null), 200)}
                className="flex-1 px-3 py-1.5 border border-bark/20 rounded text-sm bg-cream text-bark placeholder:text-wood/40"
              />
              {/* + field menu */}
              <div className="relative flex items-center">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowFieldMenu((v) => !v)}
                  onBlur={() => setTimeout(() => setShowFieldMenu(false), 150)}
                  className="flex items-center justify-center w-8 h-8 rounded border border-bark/20 text-bark hover:bg-bark/5 shrink-0"
                  title={t('emailAddField')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                </button>
                {showFieldMenu && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-cream border border-bark/20 rounded shadow-lg min-w-[140px] py-1">
                    {([
                      { key: 'cc', label: t('emailCc') },
                      { key: 'bcc', label: t('emailBcc') },
                      { key: 'replyTo', label: t('emailReplyTo') },
                    ] as const).map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setFields((p) => ({ ...p, [item.key]: !p[item.key] })); setShowFieldMenu(false); }}
                        className="flex items-center justify-between w-full px-3 py-1.5 text-sm text-bark hover:bg-bark/5 text-left"
                      >
                        <span>{item.label}</span>
                        {fields[item.key] && <span className="text-wood">✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {showSuggestions === 'to' && contactSuggestions.length > 0 && (
                <div className="absolute z-50 left-0 top-full mt-1 bg-cream border border-bark/20 rounded shadow-lg w-full max-w-md">
                  {contactSuggestions.map((s) => (
                    <button key={s.email} onMouseDown={() => applySuggestion(s, 'to')} className="block w-full text-left px-3 py-1.5 text-sm text-bark hover:bg-bark/5 border-b border-bark/5 last:border-0 truncate">
                      {s.name ? <><span className="font-medium">{s.name}</span> <span className="text-wood">{'<'}{s.email}{'>'}</span></> : s.email}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cc / Bcc / ReplyTo */}
            {fields.cc && (
              <div className="relative">
                <input type="text" placeholder={t('emailCc')} value={compose.cc}
                  onChange={(e) => { setCompose((p) => ({ ...p, cc: e.target.value })); updateSuggestions(e.target.value, 'cc'); }}
                  onBlur={() => setTimeout(() => setShowSuggestions(null), 200)}
                  className="w-full px-3 pr-8 py-1.5 border border-bark/20 rounded text-sm bg-cream text-bark placeholder:text-wood/40" />
                <button type="button" onClick={() => { setCompose((p) => ({ ...p, cc: '' })); setFields((p) => ({ ...p, cc: false })); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-wood hover:text-rust text-sm" title={t('remove')}>×</button>
                {showSuggestions === 'cc' && contactSuggestions.length > 0 && (
                  <div className="absolute z-50 left-0 top-full mt-1 bg-cream border border-bark/20 rounded shadow-lg w-full max-w-md">
                    {contactSuggestions.map((s) => (
                      <button key={s.email} onMouseDown={() => applySuggestion(s, 'cc')} className="block w-full text-left px-3 py-1.5 text-sm text-bark hover:bg-bark/5 border-b border-bark/5 last:border-0 truncate">
                        {s.name ? <><span className="font-medium">{s.name}</span> <span className="text-wood">{'<'}{s.email}{'>'}</span></> : s.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {fields.bcc && (
              <div className="relative">
                <input type="text" placeholder={t('emailBcc')} value={compose.bcc}
                  onChange={(e) => { setCompose((p) => ({ ...p, bcc: e.target.value })); updateSuggestions(e.target.value, 'bcc'); }}
                  onBlur={() => setTimeout(() => setShowSuggestions(null), 200)}
                  className="w-full px-3 pr-8 py-1.5 border border-bark/20 rounded text-sm bg-cream text-bark placeholder:text-wood/40" />
                <button type="button" onClick={() => { setCompose((p) => ({ ...p, bcc: '' })); setFields((p) => ({ ...p, bcc: false })); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-wood hover:text-rust text-sm" title={t('remove')}>×</button>
                {showSuggestions === 'bcc' && contactSuggestions.length > 0 && (
                  <div className="absolute z-50 left-0 top-full mt-1 bg-cream border border-bark/20 rounded shadow-lg w-full max-w-md">
                    {contactSuggestions.map((s) => (
                      <button key={s.email} onMouseDown={() => applySuggestion(s, 'bcc')} className="block w-full text-left px-3 py-1.5 text-sm text-bark hover:bg-bark/5 border-b border-bark/5 last:border-0 truncate">
                        {s.name ? <><span className="font-medium">{s.name}</span> <span className="text-wood">{'<'}{s.email}{'>'}</span></> : s.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {fields.replyTo && (
              <div className="relative">
                <input type="text" placeholder={t('emailReplyTo')} value={compose.replyTo}
                  onChange={(e) => setCompose((p) => ({ ...p, replyTo: e.target.value }))}
                  className="w-full px-3 pr-8 py-1.5 border border-bark/20 rounded text-sm bg-cream text-bark" />
                <button type="button" onClick={() => { setCompose((p) => ({ ...p, replyTo: '' })); setFields((p) => ({ ...p, replyTo: false })); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-wood hover:text-rust text-sm" title={t('remove')}>×</button>
              </div>
            )}

            {/* Subject */}
            <input type="text" placeholder={t('emailSubject')} value={compose.subject}
              onChange={(e) => setCompose((p) => ({ ...p, subject: e.target.value }))}
              className="w-full px-3 py-1.5 border border-bark/20 rounded text-sm bg-cream text-bark placeholder:text-wood/40" />

            {/* Editor */}
            <div className="border border-bark/20 rounded overflow-hidden flex-1 flex flex-col min-h-0">
              {/* Toolbar */}
              <div className="shrink-0 flex items-center gap-1 px-2 py-1 border-b border-bark/10 bg-bark/5">
                {!markdown && (
                  <>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }} className="px-2 py-0.5 text-sm font-bold hover:bg-bark/10 rounded text-bark" title={t('emailBold')}>B</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }} className="px-2 py-0.5 text-sm italic hover:bg-bark/10 rounded text-bark" title={t('emailItalic')}>I</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('underline'); }} className="px-2 py-0.5 text-sm underline hover:bg-bark/10 rounded text-bark" title={t('emailUnderline')}>U</button>
                    <div className="w-px h-4 bg-bark/20 mx-1" />
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('insertUnorderedList'); }} className="px-2 py-0.5 text-sm hover:bg-bark/10 rounded text-bark" title={t('emailBulletList')}>•</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('insertOrderedList'); }} className="px-2 py-0.5 text-sm hover:bg-bark/10 rounded text-bark" title={t('emailNumberedList')}>1.</button>
                    <div className="w-px h-4 bg-bark/20 mx-1" />
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); const url = prompt(t('emailPromptUrl')); if (url) document.execCommand('createLink', false, url); }} className="px-2 py-0.5 text-sm hover:bg-bark/10 rounded text-bark" title={t('emailLink')}>🔗</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); const url = prompt(t('emailPromptImageUrl') || 'Image URL:'); if (url) insertImageAtCaret(url); }} className="px-2 py-0.5 text-sm hover:bg-bark/10 rounded text-bark" title={t('emailImage') || 'Image'}>🖼</button>
                    <div className="w-px h-4 bg-bark/20 mx-1" />
                  </>
                )}
                <button type="button" onMouseDown={(e) => { e.preventDefault(); insertSignature(); }} disabled={!activeSignature.html && !activeSignature.text} className="px-2 py-0.5 text-sm hover:bg-bark/10 rounded text-bark disabled:opacity-30 disabled:cursor-not-allowed" title={t('emailInsertSignature')}>✒</button>
                <div className="w-px h-4 bg-bark/20 mx-1" />
                <button type="button" onMouseDown={(e) => { e.preventDefault(); setMarkdown(!markdown); if (!markdown) { setCompose((p) => ({ ...p, bodyHtml: '', bodyText: p.bodyText || p.bodyHtml || '' })); } }}
                  className={`px-2 py-0.5 text-xs font-mono rounded ${markdown ? 'bg-moss text-cream' : 'text-bark hover:bg-bark/10'}`} title={t('emailToggleMarkdown')}>MD</button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); setPreview((v) => !v); }}
                  className={`px-2 py-0.5 text-sm rounded ${preview ? 'bg-moss text-cream' : 'text-bark hover:bg-bark/10'}`} title={preview ? t('emailEdit') : t('emailPreview')}>{preview ? '✎' : '👁'}</button>
              </div>

              {/* Editor area */}
              {preview ? (
                <div className="email-html-viewer prose prose-sm max-w-none p-3 text-bark overflow-y-auto flex-1 min-h-0 bg-cream"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(compose.bodyHtml || '') }} />
              ) : markdown ? (
                <textarea placeholder={t('emailMarkdownPlaceholder')} value={compose.bodyText}
                  onChange={(e) => { const md = e.target.value; const html = marked.parse(md, { async: false }) as string; setCompose((p) => ({ ...p, bodyText: md, bodyHtml: html })); }}
                  className="w-full px-3 py-2 text-sm bg-cream text-bark overflow-y-auto focus:outline-none font-mono flex-1 min-h-0 resize-none" style={{ lineHeight: '1.5' }} />
              ) : (
                <div ref={editorRef} contentEditable suppressContentEditableWarning
                  onDragOver={(e) => e.preventDefault()} onDrop={handleEditorDrop} onPaste={handleEditorPaste}
                  onInput={(e) => { const html = (e.target as HTMLDivElement).innerHTML; const text = (e.target as HTMLDivElement).innerText; setCompose((p) => ({ ...p, bodyHtml: html, bodyText: text })); }}
                  className="w-full px-3 py-2 text-sm bg-cream text-bark overflow-y-auto focus:outline-none flex-1 min-h-0"
                  data-placeholder={t('emailBody')} style={{ lineHeight: '1.5' }} />
              )}
            </div>

            {/* Plain text toggle */}
            <details className="text-xs text-wood">
              <summary className="cursor-pointer hover:text-bark">{t('emailShowPlain')}</summary>
              <textarea placeholder={t('emailBody')} value={compose.bodyText}
                onChange={(e) => setCompose((p) => ({ ...p, bodyText: e.target.value, bodyHtml: '' }))}
                rows={4} className="w-full px-3 py-1.5 border border-bark/20 rounded text-sm bg-cream text-bark mt-1" />
            </details>
          </div>

          {/* Attachments sidebar */}
          <div className="w-full sm:w-64 flex-shrink-0 border-t sm:border-t-0 sm:border-l border-bark/10 flex flex-col bg-bark/5 max-h-52 sm:max-h-none">
            <label
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-moss', 'bg-moss/10'); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove('border-moss', 'bg-moss/10'); }}
              onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-moss', 'bg-moss/10'); if (e.dataTransfer.files) setFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]); }}
              className="flex-1 flex flex-col items-center justify-center gap-2 m-2 rounded-lg border-2 border-dashed border-bark/20 cursor-pointer hover:border-moss hover:bg-moss/5 transition-colors relative overflow-hidden" style={{ flexBasis: '50%' }}
            >
              <span className="text-4xl opacity-20 select-none">📎</span>
              <span className="text-xs text-wood text-center px-2 font-medium">{t('emailDropFiles')}</span>
              <input type="file" multiple onChange={(e) => { if (e.target.files) setFiles((prev) => [...prev, ...Array.from(e.target.files)]); }} className="absolute inset-0 opacity-0 cursor-pointer" />
            </label>
            {uploadProgress !== null && (
              <div className="px-2 pb-1">
                <div className="flex items-center justify-between text-xs text-moss mb-0.5">
                  <span>{t('emailUploading')}</span><span>{uploadProgress}%</span>
                </div>
                <div className="w-full h-2 bg-bark/10 rounded-full overflow-hidden">
                  <div className="h-full bg-moss rounded-full transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
            {invAttach && initial?.invoiceId != null && (
              <div className="mx-2 mb-1 rounded-lg border border-wheat/50 bg-cream/60 px-2.5 py-2 shrink-0">
                <div className="text-xs font-semibold text-bark mb-1">🧾 {t('invSendAttachments')}{initial.invoiceNumber ? ` ${initial.invoiceNumber}` : ''}</div>
                <select value={invAttach.template} onChange={e => setInvAttach(a => a && { ...a, template: e.target.value })}
                  className="w-full mb-1.5 p-1 border border-wheat rounded text-xs bg-linen/50">
                  <option value="issued-invoice">{t('invSendTemplateIssued')}</option>
                  <option value="incoming-invoice">{t('invSendTemplateIncoming')}</option>
                </select>
                {([['pdf', t('invSendAttachPdf')], ['isdoc', t('invSendAttachIsdoc')], ['isdocx', t('invSendAttachIsdocx')], ['zugferd', t('invSendAttachZugferd')]] as const).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-1.5 text-xs text-bark">
                    <input type="checkbox" checked={invAttach[k]} onChange={e => setInvAttach(a => a && { ...a, [k]: e.target.checked })} />{label}
                  </label>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-2 pb-2">
              {files.length > 0 ? (
                <div className="space-y-1.5">
                  {files.map((file, i) => {
                    const sizeStr = file.size < 1024 ? `${file.size} B` : file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)} KB` : `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
                    const ext = file.name.split('.').pop()?.toUpperCase().slice(0, 4) || 'FILE';
                    return (
                      <div key={i} className="flex items-center gap-2 text-sm text-bark bg-cream rounded px-2 py-2 border border-bark/10">
                        <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-bark/10 rounded text-[9px] font-bold text-bark/70">{ext}</span>
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium" title={file.name}>{file.name}</div>
                          <div className="text-xs text-wood">{sizeStr}</div>
                        </div>
                        <button
                          onClick={() => { const url = URL.createObjectURL(file); const a = document.createElement('a'); a.href = url; a.download = file.name; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); }}
                          className="text-moss hover:text-moss/70 flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-moss/10" title={t('emailDownloadAttachment')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                        </button>
                        <button onClick={() => setFiles((f) => f.filter((_, idx) => idx !== i))} className="text-rust hover:text-rust/70 flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-rust/10" title={t('emailRemoveAttachment')}>✕</button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-wood italic text-center mt-2">{t('emailNoAttachments')}</p>
              )}
            </div>
            {/* Options */}
            <div className="px-3 py-2 border-t border-bark/10">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={returnReceipt} onChange={(e) => setReturnReceipt(e.target.checked)} className="accent-moss w-4 h-4" />
                <span className="text-xs text-bark font-medium">📧 {t('emailReturnReceipt')}</span>
              </label>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-xs text-bark font-medium">⚡ {t('emailPriority')}:</span>
                <select value={priority} onChange={(e) => setPriority(e.target.value as 'highest' | 'high' | 'normal' | 'low' | 'lowest')} className="text-xs bg-cream text-bark border border-bark/20 rounded px-1.5 py-0.5">
                  <option value="highest">🔴 {t('emailPriorityHighest')}</option>
                  <option value="high">🟠 {t('emailPriorityHigh')}</option>
                  <option value="normal">⚪ {t('emailPriorityNormal')}</option>
                  <option value="low">🔵 {t('emailPriorityLow')}</option>
                  <option value="lowest">⚪ {t('emailPriorityLowest')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-bark/10">
          <button onClick={handleClose} disabled={sending} className="px-4 py-1.5 text-sm text-wood hover:text-bark disabled:opacity-50">{t('emailCancel')}</button>
          <button onClick={handleSaveDraft} disabled={!compose.subject.trim()} className="px-4 py-1.5 text-sm text-wood border border-bark/20 rounded hover:bg-bark/5 transition disabled:opacity-50" title={t('emailSaveDraft')}>{t('emailSaveDraft')}</button>
          <button
            onClick={handleSend}
            disabled={!compose.to || !compose.subject || sending}
            title={(!compose.to || !compose.subject) ? t('emailSendDisabledHint') : t('emailSend')}
            className="px-4 py-1.5 text-sm bg-moss text-cream rounded hover:bg-moss/80 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {sending ? (<><span className="inline-block w-3 h-3 border-2 border-cream/30 border-t-cream rounded-full animate-spin" />{t('emailSending')}</>) : t('emailSend')}
          </button>
          {!maximized && (
            <div
              onMouseDown={startResize}
              title={t('emailResize')}
              className="self-end -mb-2 -mr-2 cursor-nwse-resize text-wood/40 hover:text-moss shrink-0 transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M3 13 L13 3" /><path d="M7 13 L13 7" /><path d="M11 13 L13 11" />
              </svg>
            </div>
          )}
        </div>

        {/* Sending overlay */}
        {sending && (
          <div className="absolute inset-0 bg-cream/60 backdrop-blur-sm flex flex-col items-center justify-center gap-3 z-10 rounded-lg">
            <span className="inline-block w-10 h-10 border-4 border-moss/30 border-t-moss rounded-full animate-spin" />
            <span className="text-sm font-medium text-moss">{t('emailSending')}</span>
          </div>
        )}

        {/* Discard dialog */}
        {(showDiscard || discardExiting) && (
          <div className={`fixed inset-0 z-[10000] flex items-center justify-center bg-bark/50 backdrop-blur-sm ${showDiscard ? 'animate-backdropIn' : 'animate-backdropOut'}`} onClick={() => setShowDiscard(false)}>
            <div role="dialog" aria-modal="true" className={`bg-cream rounded-xl shadow-2xl border border-bark/15 w-full max-w-md p-6 ${showDiscard ? 'animate-modalIn' : 'animate-modalOut'}`} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-rust/15 flex items-center justify-center text-xl flex-shrink-0">⚠️</div>
                <div>
                  <h3 className="text-sm font-bold text-bark">{t('emailDiscardTitle')}</h3>
                  <p className="text-xs text-wood mt-0.5">{t('emailDiscardBody')}</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowDiscard(false)} className="px-4 py-1.5 text-sm text-wood border border-bark/20 rounded hover:bg-bark/5">{t('emailCancel')}</button>
                <button onClick={() => { setShowDiscard(false); onClose(); }} className="px-4 py-1.5 text-sm bg-rust text-cream rounded hover:bg-rust/80">{t('emailDiscard')}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
